import { Facebook, Instagram, Linkedin, Music, Twitter, Youtube, type LucideIcon } from "lucide-react";

/**
 * Single source of truth for URL-hostname → social platform detection, shared by the landing page
 * builder (team member links, footer links) and the email builder's Social block. Each of those
 * consumers used to keep its own copy of this domain-pattern table; they drifted (the email builder's
 * copy was missing "tiktok" and had no generic fallback), so a URL that one builder recognized the
 * other silently mis-tagged. Detection here only covers platforms identifiable from a URL — "email"
 * and any generic/"other" fallback are consumer-specific and layered on top by each caller.
 */
export const DOMAIN_SOCIAL_PLATFORMS = ["linkedin", "twitter", "instagram", "facebook", "youtube", "tiktok"] as const;
export type DomainSocialPlatform = (typeof DOMAIN_SOCIAL_PLATFORMS)[number];

const DOMAIN_PATTERNS: Record<DomainSocialPlatform, string[]> = {
  linkedin: ["linkedin.com"],
  twitter: ["twitter.com", "x.com"],
  instagram: ["instagram.com"],
  facebook: ["facebook.com", "fb.com", "fb.me"],
  youtube: ["youtube.com", "youtu.be"],
  tiktok: ["tiktok.com"],
};

/** Icon shown for each domain-detectable platform — shared so the landing page and email builders'
 *  pickers render the exact same glyph per platform. */
export const DOMAIN_SOCIAL_PLATFORM_ICON: Record<DomainSocialPlatform, LucideIcon> = {
  linkedin: Linkedin,
  twitter: Twitter,
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  tiktok: Music,
};

/** Matches a bare email address (e.g. "you@example.com") — not a URL. */
export const BARE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Prepends `https://` to a bare domain/path so `new URL()` can parse it.
 *
 * @param url - The raw URL string (e.g. "linkedin.com/in/user")
 * @returns The URL with an `https://` prefix, or the trimmed input unchanged if already has one
 */
export function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Detects a known social platform from a URL's hostname.
 *
 * @param url - The raw URL/domain to analyze (protocol optional)
 * @returns The matching platform, or `null` if the URL is empty, unparseable, or from a domain none
 *   of the known platforms match — callers decide what a `null` result falls back to.
 */
export function detectDomainSocialPlatform(url: string): DomainSocialPlatform | null {
  if (!url.trim()) return null;
  try {
    const hostname = new URL(normalizeUrl(url)).hostname.toLowerCase();
    return (
      DOMAIN_SOCIAL_PLATFORMS.find((platform) =>
        DOMAIN_PATTERNS[platform].some((domain) => hostname.includes(domain))
      ) ?? null
    );
  } catch {
    return null;
  }
}
